import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher, LocationId, ActionCompletionToast, InsanityResult } from '../game/types';
import { MovementSystem } from './MovementSystem';

export class SanitySystem {
  constructor(
    private gameState: GameStateManager,
    private gameData: GameData,
    private movementSystem: MovementSystem
  ) {}

  updateSanity(): void {
    const researchers = this.gameState.getResearchers();
    const sanityDamageMap = new Map<string, number>(); // researcherId -> total damage
    const config = this.gameData.insanityConfig;

    // First pass: check each researcher with Memetic Contagion for random chance to spread
    for (const researcher of researchers) {
      const maxSanity = researcher.data.sanity;
      const currentSanity = researcher.currentSanity;
      const relativeSanity = currentSanity / maxSanity;

      // Check if researcher has Memetic Contagion (sanity < threshold)
      if (relativeSanity < config.insane_threshold) {
        // Calculate chance: linear interpolation between threshold and zero
        // At threshold (50%): chance_at_threshold (10% = every 10 seconds on average)
        // At 0%: chance_at_zero (20% = every 5 seconds on average)
        // Formula: chance = chance_at_threshold + (threshold - relativeSanity) / threshold * (chance_at_zero - chance_at_threshold)
        const chance = config.chance_at_threshold + 
          (config.insane_threshold - relativeSanity) / config.insane_threshold * 
          (config.chance_at_zero - config.chance_at_threshold);
        
        // Roll random chance - only spread if chance succeeds
        if (Math.random() < chance) {
          // Get source location (current location or target if in transit)
          const sourceLocation = this.getEffectiveLocation(researcher);

          // Spread to all other researchers
          for (const targetResearcher of researchers) {
            if (targetResearcher.id === researcher.id) {
              continue; // Don't spread to self
            }

            // Get target location (current location or target if in transit)
            const targetLocation = this.getEffectiveLocation(targetResearcher);

            // Calculate distance
            let distance: number;
            if (targetResearcher.targetLocation !== null) {
              // Researcher is in transit - use transit distance
              distance = config.transit_distance;
            } else if (sourceLocation && targetLocation) {
              distance = this.movementSystem.calculateDistance(sourceLocation, targetLocation);
            } else {
              // Can't determine distance, skip
              continue;
            }

            // Calculate sanity loss: base_damage reduced by 1 per distance, minimum 1
            const sanityLoss = Math.max(1, config.base_damage - distance);

            // Accumulate damage for this target
            const currentDamage = sanityDamageMap.get(targetResearcher.id) || 0;
            sanityDamageMap.set(targetResearcher.id, currentDamage + sanityLoss);
          }

          // Queue toast notification when spread occurs
          const strings = this.gameData.uiStrings;
          const toast: ActionCompletionToast = {
            researcherName: researcher.data.name,
            location: sourceLocation || 'control_room',
            actionType: 'Research', // Using Research as action type for toast styling
            summary: `${researcher.data.name} is speaking forbidden knowledge on the intracom`,
            timestamp: Date.now()
          };
          this.gameState.queueToast(toast);
        }
      }
    }

    // Second pass: apply sanity damage
    for (const [researcherId, totalDamage] of sanityDamageMap.entries()) {
      const researcher = researchers.find(r => r.id === researcherId);
      if (researcher) {
        // Apply damage (already integer values, no rounding needed)
        const newSanity = Math.max(0, researcher.currentSanity - totalDamage);
        researcher.currentSanity = newSanity;

        // Mark researcher for visual effect
        researcher.sanityDamageEffect = true;
      }
    }

    // Third pass: check for deaths at 0 sanity
    for (const researcher of researchers) {
      if (researcher.currentSanity <= 0) {
        this.handleDeath(researcher, sanityDamageMap);
      }
    }
  }

  private getEffectiveLocation(researcher: Researcher): LocationId | null {
    // If in transit, use target location; otherwise use current location
    if (researcher.targetLocation !== null) {
      return researcher.targetLocation;
    }
    return researcher.location;
  }

  private handleDeath(researcher: Researcher, sanityDamageMap: Map<string, number>): void {
    // Store information before removal
    const researcherName = researcher.data.name;
    const deathLocation = this.getEffectiveLocation(researcher) || researcher.location || 'control_room';
    const config = this.gameData.insanityConfig;
    
    // Note: Action cancellation is handled by clearing the action fields directly
    // This is done before removal to ensure clean state

    // Trigger final Memetic Contagion spread from dead researcher
    const maxSanity = researcher.data.sanity;
    const relativeSanity = 0; // Dead researcher has 0 sanity

    const researchers = this.gameState.getResearchers();
    const finalDamageMap = new Map<string, number>(); // researcherId -> damage from this death

    for (const targetResearcher of researchers) {
      if (targetResearcher.id === researcher.id) {
        continue; // Don't spread to self
      }

      const targetLocation = this.getEffectiveLocation(targetResearcher);

      let distance: number;
      if (targetResearcher.targetLocation !== null) {
        distance = config.transit_distance; // In transit
      } else if (deathLocation && targetLocation) {
        distance = this.movementSystem.calculateDistance(deathLocation, targetLocation);
      } else {
        continue;
      }

      // Calculate sanity loss: base_damage reduced by 1 per distance, minimum 1, then multiplied by 4 for death spread
      const sanityLoss = Math.max(1, config.base_damage - distance) * 4;
      finalDamageMap.set(targetResearcher.id, sanityLoss);

      // Apply damage
      targetResearcher.currentSanity = Math.max(0, targetResearcher.currentSanity - sanityLoss);
    }

    // Remove researcher
    this.gameState.removeResearcher(researcher.id);

    // Check if all researchers are dead
    const remainingResearchers = this.gameState.getResearchers();
    if (remainingResearchers.length === 0) {
      // All researchers dead - trigger defeat
      this.gameState.setGameEndState('defeat', 'researchers');
      return; // Don't show insanity modal, the defeat modal will show
    }

    // Create InsanityResult
    const strings = this.gameData.uiStrings;
    const locationName = strings.locations?.[deathLocation] || deathLocation;
    
    // Build list of affected researchers and their damage
    const affectedResearchers: Array<{ name: string; damage: number }> = [];
    for (const [targetId, damage] of finalDamageMap.entries()) {
      const target = researchers.find(r => r.id === targetId);
      if (target && damage > 0) {
        affectedResearchers.push({
          name: target.data.name,
          damage: Math.round(damage * 100) / 100 // Round to 2 decimal places
        });
      }
    }

    const insanityResult: InsanityResult = {
      researcherName: researcherName,
      location: deathLocation,
      locationName: locationName,
      reason: strings.messages?.insanity_reason || 'Insanity - Sanity reached 0',
      affectedResearchers: affectedResearchers,
      totalSanityDamage: Array.from(finalDamageMap.values()).reduce((sum, d) => sum + d, 0)
    };

    // Queue pending insanity result
    this.gameState.queueInsanity(insanityResult);
  }
}
